import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container, CircularProgress, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import SpellingLogo from '../../../assets/img/spellingapp-logo.svg';
import LogoMember from '../../../assets/img/Logo-member.png';
import { authen, db } from '../../firebase/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { setProduct } from 'shared/store/src/lib/actions/products';
import RemoteErrorLog from '../../Utils/api';
import { createCheckoutSession } from '../../stripe/createCheckoutSession';
import { httpsCallable, getFunctions } from 'firebase/functions';
import CustomNoResponseModal from '../../components/CustomNoResponseModal';
import axios from 'axios';
import './Subscription.scss';
import { environment } from '../../../environments/environment';
import { styled, keyframes } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import SupportIcon from '@mui/icons-material/Support';

// Type definitions
interface SubscriptionData {
  productId: string;
  priceId: string;
  price: {
    unit_amount: number;
  };
  product: {
    name: string;
  };
}

interface Plan {
  title: string;
  description: string;
  price: string;
  currency: string;
  period: string;
  priceId: string | null;
  popular: boolean;
}

// Enhanced animations
const fadeInUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(40px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const slideInLeft = keyframes`
  from { 
    opacity: 0; 
    transform: translateX(-30px); 
  }
  to { 
    opacity: 1; 
    transform: translateX(0); 
  }
`;

const slideInRight = keyframes`
  from { 
    opacity: 0; 
    transform: translateX(30px); 
  }
  to { 
    opacity: 1; 
    transform: translateX(0); 
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  25% { transform: translateY(-8px) rotate(1deg); }
  50% { transform: translateY(-4px) rotate(0deg); }
  75% { transform: translateY(-12px) rotate(-1deg); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

// Modern gradient background
const MainContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  width: '100%',
  paddingTop: '120px', // Increased from 80px for better spacing
  paddingBottom: '40px',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  position: 'relative',
  background: `
    linear-gradient(135deg, 
      #fafbfc 0%, 
      #f8fffe 25%,
      #f5fff7 50%,
      #f8fffe 75%,
      #fafbfc 100%
    ),
    radial-gradient(ellipse at top left, rgba(42, 146, 53, 0.1) 0%, transparent 50%),
    radial-gradient(ellipse at bottom right, rgba(76, 175, 80, 0.08) 0%, transparent 50%)
  `,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      repeating-linear-gradient(
        90deg,
        transparent,
        transparent 100px,
        rgba(42, 146, 53, 0.03) 100px,
        rgba(42, 146, 53, 0.03) 101px
      )
    `,
    pointerEvents: 'none',
  }
}));

// Glassmorphism container
const GlassContainer = styled(Card)(({ theme }) => ({
  background: 'rgba(248, 255, 249, 0.9)',
  backdropFilter: 'blur(20px)',
  borderRadius: '28px',
  border: '1px solid rgba(42, 146, 53, 0.2)',
  boxShadow: `
    0 25px 50px rgba(42, 146, 53, 0.15),
    0 12px 40px rgba(0, 0, 0, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.9)
  `,
  position: 'relative',
  overflow: 'visible',
  animation: `${fadeInUp} 0.8s ease-out`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -2,
    left: -2,
    right: -2,
    bottom: -2,
    background: 'linear-gradient(135deg, rgba(42, 146, 53, 0.2) 0%, rgba(76, 175, 80, 0.1) 100%)',
    borderRadius: 'inherit',
    zIndex: -1,
    opacity: 1,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    background: 'rgba(245, 255, 247, 0.95)',
    border: '1px solid rgba(42, 146, 53, 0.3)',
    boxShadow: `
      0 30px 60px rgba(42, 146, 53, 0.2),
      0 15px 50px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.95)
    `,
    transform: 'translateY(-3px)',
    '&::before': {
      opacity: 1,
    }
  }
}));

// Floating logo container
const LogoContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '20px',
  animation: `${float} 6s ease-in-out infinite`,
  '&::after': {
    content: '""',
    position: 'absolute',
    width: '140px',
    height: '140px',
    background: 'radial-gradient(circle, rgba(42, 146, 53, 0.1) 0%, transparent 70%)',
    borderRadius: '50%',
    zIndex: -1,
    animation: `${pulse} 4s ease-in-out infinite`,
  }
}));

// Modern benefit card
const BenefitCard = styled(Card)(({ theme }) => ({
  height: '160px', // Increased height for better content fit
  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 255, 249, 0.8) 100%)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(42, 146, 53, 0.08)',
  borderRadius: '16px',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  minWidth: '180px', // Minimum width to prevent cramping
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '2px',
    background: 'linear-gradient(90deg, transparent 0%, rgba(42, 146, 53, 0.6) 50%, transparent 100%)',
    transform: 'translateX(-100%)',
    transition: 'transform 0.6s ease',
  },
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 20px 40px rgba(42, 146, 53, 0.15)',
    border: '1px solid rgba(42, 146, 53, 0.2)',
    '&::before': {
      transform: 'translateX(100%)',
    }
  }
}));

// Premium action button
const PremiumButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  padding: '16px 32px',
  background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 50%, #2A9235 100%)',
  backgroundSize: '200% 200%',
  color: '#fff',
  fontSize: '18px',
  fontWeight: 700,
  borderRadius: '16px',
  textTransform: 'none',
  boxShadow: `
    0 12px 32px rgba(42, 146, 53, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2)
  `,
  border: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: `
      linear-gradient(90deg, 
        transparent, 
        rgba(255, 255, 255, 0.4), 
        transparent
      )
    `,
    animation: `${shimmer} 2s infinite`,
  },
  '&:hover': {
    backgroundPosition: '100% 0',
    transform: 'translateY(-2px) scale(1.02)',
    boxShadow: `
      0 16px 40px rgba(42, 146, 53, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.3)
    `,
  },
  '&:active': {
    transform: 'translateY(0) scale(0.98)',
  }
}));

// Animated tab button
const TabButton = styled(Button)<{ selected?: boolean }>(({ selected }) => ({
  position: 'relative',
  borderRadius: '20px',
  padding: '12px 24px',
  textTransform: 'uppercase',
  fontSize: '0.85rem',
  fontWeight: 600,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  border: 'none',
  background: selected 
    ? 'linear-gradient(135deg, #2a9235 0%, #4CAF50 100%)' 
    : 'rgba(255, 255, 255, 0.8)',
  color: selected ? 'white' : '#333',
  backdropFilter: 'blur(8px)',
  boxShadow: selected 
    ? '0 8px 24px rgba(42, 146, 53, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)' 
    : '0 4px 12px rgba(0, 0, 0, 0.05)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: selected ? 0 : '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%)',
    transition: 'left 0.3s ease',
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: selected 
      ? '0 12px 32px rgba(42, 146, 53, 0.35)' 
      : '0 8px 20px rgba(0, 0, 0, 0.1)',
  }
}));

// Price display with animation
const PriceDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: '6px',
  marginBottom: '16px',
  animation: `${slideInLeft} 0.6s ease-out 0.3s both`,
  '& .price': {
    fontSize: '32px',
    fontWeight: 800,
    background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    lineHeight: 1,
  },
  '& .period': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#666',
    opacity: 0.8,
  }
}));

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(0);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [monthlySubscription, setMonthlySubscription] = useState<SubscriptionData | null>(null);
  const [quarterlySubscription, setQuarterlySubscription] = useState<SubscriptionData | null>(null);
  const [yearlySubscription, setYearlySubscription] = useState<SubscriptionData | null>(null);
  const [showUpgradePopup, setShowUpgradePopup] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [subscription, setSubscription] = useState('');
  const [user, setUser] = useState<any>(null);
  const [isManageSubscriptionLoading, setIsManageSubscriptionLoading] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState<string>();
  const [isQuestionModalVisible, setIsQuestionModalVisible] = useState(false);

  const products = useSelector((state: any) => state.products);
  const dispatch = useDispatch();

  const fetchPrices = async () => {
    try {
      const productsRef = collection(db, 'products');
      const querySnapshot = await getDocs(productsRef);

      const allPrices: any[] = [];

      const priceFetchPromises = querySnapshot.docs.map(async (productDoc) => {
        const productData = productDoc.data();
        if (productData.active) {
          const pricesRef = collection(productDoc.ref, 'prices');
          const pricesSnapshot = await getDocs(pricesRef);

          const productPrices = pricesSnapshot.docs.map((priceDoc) => ({
            productId: productDoc.id,
            priceId: priceDoc.id,
            price: priceDoc.data(),
            product: productDoc.data(),
          }));

          allPrices.push(...productPrices);
        }
      });

      await Promise.all(priceFetchPromises);
      dispatch(setProduct(allPrices));
    } catch (error) {
      RemoteErrorLog(error, 'Subscription.tsx');
    }
  };

  const fetchCurrency = async () => {
    try {
      const [currencyResponse] = await Promise.all([
        axios.get(`${environment.backendUrl}/api/get-currency`)
      ]);
  
      if (currencyResponse.data.status === 'success') {
        setCurrency(currencyResponse.data.currency);
      }
    } catch (error) {
      RemoteErrorLog(error, 'Subscription.tsx - fetchCurrency');
      setCurrency('USD');
    }
  };

  const formatPrice = (amount: number) => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    });
    return formatter.format(amount);
  };

  const convertUSDToINR = (usdAmount: number) => {
    // Convert USD to INR (assuming 1 USD = 80 INR, you can adjust this rate)
    const usdToInrRate = 80;
    return usdAmount * usdToInrRate;
  };

  const formatPriceForUser = (amount: number) => {
    if (currency === 'INR') {
      const inrAmount = convertUSDToINR(amount);
      return `₹${inrAmount.toFixed(0)}`;
    } else {
      return formatPrice(amount);
    }
  };
  
  const defaultPlans: Plan[] = [
    {
      title: 'Monthly',
      description: 'Perfect for trying out all premium features with complete flexibility.',
      price: currency === 'INR' ? '₹360' : '$4.5',
      currency: currency === 'INR' ? 'INR' : 'USD',
      period: 'Per Month',
      priceId: null,
      popular: false
    },
    {
      title: 'Quarterly',
      description: 'Great value for consistent learners. Save 10% with our 3-month commitment.',
      price: currency === 'INR' ? '₹972' : '$12.15',
      currency: currency === 'INR' ? 'INR' : 'USD',
      period: 'Every 3 Months',
      priceId: null,
      popular: true
    },
    {
      title: 'Yearly',
      description: 'Best value for dedicated learners. Save 20% with our annual membership.',
      price: currency === 'INR' ? '₹3600' : '$45',
      currency: currency === 'INR' ? 'INR' : 'USD',
      period: 'Per Year',
      priceId: null,
      popular: false
    }
  ];

  const benefits = [
    {
      icon: <CheckCircleIcon sx={{ fontSize: 24, color: '#2A9235' }} />,
      title: 'Unlimited Access',
      description: 'Keep using all features after your 7‑day free trial ends.'
    },
    {
      icon: <StarIcon sx={{ fontSize: 24, color: '#2A9235' }} />,
      title: 'Full Feature Set',
      description: 'Enjoy every tool, mode, and capability without limits.'
    },
    {
      icon: <SupportIcon sx={{ fontSize: 24, color: '#2A9235' }} />,
      title: 'Priority Support',
      description: 'Get faster help whenever you need it.'
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 24, color: '#2A9235' }} />,
      title: 'Save Your Progress',
      description: 'Keep your saved work, history, and achievements.'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 24, color: '#2A9235' }} />,
      title: 'Seamless Experience',
      description: 'Continue learning without interruptions or restrictions.'
    }
  ];

  useEffect(() => {
    fetchCurrency();
    fetchPrices();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(authen, (currentUser) => {
      setUser(currentUser); // Update the user state
      const checkPremium = async () => {
        const token = await (authen?.currentUser as any)?.getIdToken(true);
        const decodedToken: any = await authen?.currentUser?.getIdTokenResult();

        const value = decodedToken?.claims?.stripeRole
          ? true
          : false ||
            (decodedToken?.claims?.revenueCatEntitlements &&
              decodedToken?.claims?.revenueCatEntitlements?.[0])
          ? true
          : false;
        let subscription = '';
        setIsPremium(value);
        if (value) {
          if (decodedToken?.claims?.stripeRole) {
            subscription = decodedToken?.claims?.stripeRole.includes('monthly')
              ? 'Monthly'
              : decodedToken?.claims?.stripeRole.includes('quarterly')
              ? 'Quarterly'
              : 'Yearly';
            setSubscription(subscription);
          } else if (decodedToken?.claims?.revenueCatEntitlements?.[0]) {
            subscription =
              decodedToken?.claims?.revenueCatEntitlements?.[0].includes(
                'monthly'
              )
                ? 'Monthly'
                : decodedToken?.claims?.revenueCatEntitlements?.[0].includes(
                    'quarterly'
                  )
                ? 'Quarterly'
                : 'Yearly';
            setSubscription(subscription);
          }
        }
      };
      checkPremium();
    });
    // Clean up the listener when the component unmounts
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (products) {
      products.forEach((element: any) => {
        if (element?.product?.name === 'Ad-Free Monthly') {
          setMonthlySubscription(element);
        }
        if (element?.product?.name === 'Ad-Free Quarterly') {
          setQuarterlySubscription(element);
        }
        if (element?.product?.name === 'Ad-Free Yearly') {
          setYearlySubscription(element);
        }
      });
    }
  }, [products]);

  const handleTabChange = (index: number) => {
    setSelectedTab(index);
  };

  const handleUpgradeClick = () => {
    if (!authen?.currentUser?.uid) {
      setShowUpgradePopup(true);
      return;
    }
    // If user is logged in, proceed with checkout
    handleCheckout();
  };

  const handleCheckout = () => {
    let priceId;
    switch (selectedTab) {
      case 0:
        priceId = monthlySubscription?.priceId;
        break;
      case 1:
        priceId = quarterlySubscription?.priceId;
        break;
      case 2:
        priceId = yearlySubscription?.priceId;
        break;
    }

    if (!priceId) {
      console.error('No price ID available');
      return;
    }

    setIsCheckoutLoading(true);
    handleCheckoutSession(authen.currentUser?.uid || '', priceId);
  };

  const handleLoginAndContinue = () => {
    setShowUpgradePopup(false);
    navigate('/login', { 
      state: { 
        from: 'subscription',
        redirectTo: '/profile',
        openMembershipTab: true,
        tabIndex: 3
      } 
    });
  };

  const handleCancelUpgrade = () => {
    setShowUpgradePopup(false);
  };

  const handleCheckoutSession = async (uid: string, priceId: string) => {
    try {
      const result = await createCheckoutSession(uid, priceId);
      if (result?.success === false) {
        setIsCheckoutLoading(false);
      }
    } catch (error) {
      console.error('Checkout session error:', error);
      setIsCheckoutLoading(false);
    }
  };

  const manageMembership = async () => {
    setIsManageSubscriptionLoading(true);
  
    try {
      const decodedToken: any = await authen?.currentUser?.getIdTokenResult();
      
      if (decodedToken?.claims?.stripeRole) {
        redirectToCustomerPortal();
      } else {
        setIsManageSubscriptionLoading(false);
        setIsQuestionModalVisible(true);
        setApiErrorMessage("No active subscription found. Please subscribe to access membership management.");
      }
    } catch (error) {
      setIsManageSubscriptionLoading(false);
      RemoteErrorLog(error, 'manageMembership');
    }
  };

  const redirectToCustomerPortal = async () => {
    try {
      const functions = getFunctions();
      const createPortalLink = httpsCallable(
        functions,
        'ext-firestore-stripe-payments-createPortalLink'
      );
    
      const result = await createPortalLink({
        returnUrl: `${environment.baseUrl}subscription`, 
      });
  
      const { url }: any = result.data;
      setIsManageSubscriptionLoading(false);
      window.location.assign(url);
    } catch (error) {
      setIsManageSubscriptionLoading(false);
      console.error('Error redirecting to customer portal:', error);
      RemoteErrorLog(error, 'redirectToCustomerPortal');
    }
  };
  

  const hideNoQuestionModal = () => {
    setIsQuestionModalVisible(false);
  };

  const getCurrentPlan = (): Plan | null => {
    if (!authen?.currentUser?.uid) {
      return defaultPlans[selectedTab];
    }

    switch (selectedTab) {
      case 0:
        return monthlySubscription ? {
          title: defaultPlans[0].title,
          description: defaultPlans[0].description,
          price: formatPriceForUser(monthlySubscription.price.unit_amount / 100),
          currency: currency,
          period: defaultPlans[0].period,
          priceId: monthlySubscription.priceId,
          popular: defaultPlans[0].popular
        } : null;
      case 1:
        return quarterlySubscription ? {
          title: defaultPlans[1].title,
          description: defaultPlans[1].description,
          price: formatPriceForUser(quarterlySubscription.price.unit_amount / 100),
          currency: currency,
          period: defaultPlans[1].period,
          priceId: quarterlySubscription.priceId,
          popular: defaultPlans[1].popular
        } : null;
      case 2:
        return yearlySubscription ? {
          title: defaultPlans[2].title,
          description: defaultPlans[2].description,
          price: formatPriceForUser(yearlySubscription.price.unit_amount / 100),
          currency: currency,
          period: defaultPlans[2].period,
          priceId: yearlySubscription.priceId,
          popular: defaultPlans[2].popular
        } : null;
      default:
        return defaultPlans[selectedTab];
    }
  };

  const currentPlan = getCurrentPlan();

  return (
    <>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        position: 'relative'
      }}>
        <Navbar currentPage="subscription"/>
        <MainContainer>
          <Container maxWidth="xl">
            <GlassContainer sx={{ maxWidth: '1200px', mx: 'auto', p: 0 }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                {/* Header Section */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  {/* <LogoContainer>
                    <img src={SpellingLogo} alt="logo-spelling" style={{ height: 'auto', width: '140px' }} />
                  </LogoContainer> */}
                  
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: '24px', md: '28px' },
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 2,
                      animation: `${fadeInUp} 0.8s ease-out 0.2s both`
                    }}
                  >
                    Membership Benefits
                  </Typography>
                </Box>

                {/* Benefits Grid */}
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
                  gap: { xs: 2, sm: 2.5, md: 3 },
                  mb: 3,
                  px: { xs: 1, sm: 2 }
                }}>
                  {benefits.map((benefit, index) => (
                    <BenefitCard 
                      key={index}
                      sx={{ 
                        animation: `${fadeInUp} 0.6s ease-out ${0.1 * index + 0.3}s both`
                      }}
                    >
                      <CardContent sx={{ 
                        p: 2, 
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        height: '100%',
                        minHeight: '160px'
                      }}>
                        <Box sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '32px',
                        }}>
                          {benefit.icon}
                        </Box>
                        <Box sx={{ 
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flex: 1,
                          minHeight: '60px'
                        }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: '16px',
                              fontWeight: 700,
                              color: '#2A9235',
                              mb: 2,
                              textAlign: 'center',
                              lineHeight: 1.2,
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {benefit.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '14px',
                              color: '#666',
                              lineHeight: 1.4,
                              textAlign: 'center',
                              maxWidth: '100%',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {benefit.description}
                          </Typography>
                        </Box>
                      </CardContent>
                    </BenefitCard>
                  ))}
                </Box>

                {/* Plan Selection or Premium Status */}
                <Box sx={{ animation: `${slideInRight} 0.8s ease-out 0.5s both` }}>
                  {isPremium ? (
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: { xs: '20px', md: '24px' },
                        fontWeight: 700,
                        color: '#2A9235',
                        textAlign: 'center',
                        mb: 2
                      }}
                    >
                      Your Premium Membership is Active
                    </Typography>
                  ) : (
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: { xs: '20px', md: '24px' },
                        fontWeight: 700,
                        color: '#23A6F0',
                        textAlign: 'center',
                        mb: 2
                      }}
                    >
                      Choose Your Plan
                    </Typography>
                  )}

                  {/* Tab Selector - Only show for non-premium users */}
                  {!isPremium && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      mb: 2,
                      gap: 1,
                      p: 1,
                      backgroundColor: 'rgba(243, 243, 243, 0.6)',
                      backdropFilter: 'blur(8px)',
                      borderRadius: '24px',
                      border: '1px solid rgba(42, 146, 53, 0.1)',
                      boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.02)'
                    }}>
                      {defaultPlans.map((plan, index) => (
                        <TabButton
                          key={index}
                          selected={selectedTab === index}
                          onClick={() => handleTabChange(index)}
                          sx={{
                            minWidth: { xs: '100px', md: '140px' },
                            fontSize: { xs: '0.75rem', md: '0.85rem' }
                          }}
                        >
                          {plan.title}
                        </TabButton>
                      ))}
                    </Box>
                  )}

                  {/* Plan Details or Premium Status */}
                  {isPremium ? (
                    <Box sx={{ 
                      textAlign: 'center',
                      background: 'linear-gradient(145deg, rgba(248, 255, 249, 0.8) 0%, rgba(255, 255, 255, 0.9) 100%)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '20px',
                      p: 4,
                      border: '1px solid rgba(42, 146, 53, 0.1)',
                      mb: 2,
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: 'linear-gradient(90deg, #2A9235 0%, #4CAF50 100%)',
                      }
                    }}>
                      <Box sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        mb: 2
                      }}>
                        <CheckCircleIcon sx={{ fontSize: 48, color: '#2A9235' }} />
                        <Box>
                          <Typography
                            variant="h5"
                            sx={{
                              fontSize: { xs: '20px', md: '24px' },
                              fontWeight: 800,
                              color: '#2A9235',
                              mb: 1
                            }}
                          >
                            {subscription} Premium Plan
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: '16px',
                              color: '#555',
                              fontWeight: 500
                            }}
                          >
                            You have full access to all premium features
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Typography
                        sx={{
                          fontSize: '16px',
                          color: '#555',
                          fontWeight: 500,
                          mb: 3,
                          textAlign: 'center',
                          lineHeight: 1.4
                        }}
                        variant="body1"
                      >
                        View invoices, update subscription & payment methods
                      </Typography>

                      {isManageSubscriptionLoading ? (
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          py: 2 
                        }}>
                          <CircularProgress 
                            size={40} 
                            sx={{ 
                              color: '#2A9235',
                              mb: 1
                            }} 
                          />
                          <Typography 
                            sx={{ 
                              color: '#666',
                              fontSize: '14px',
                              fontWeight: 500
                            }}
                          >
                            Processing...
                          </Typography>
                        </Box>
                      ) : (
                        <PremiumButton
                          onClick={manageMembership}
                          sx={{
                            mt: 2,
                            maxWidth: '300px',
                            mx: 'auto',
                            display: 'block',
                            animation: `${fadeInUp} 0.6s ease-out 0.4s both`
                          }}
                        >
                          Manage membership
                        </PremiumButton>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ 
                      textAlign: 'center',
                      background: 'linear-gradient(145deg, rgba(248, 255, 249, 0.8) 0%, rgba(255, 255, 255, 0.9) 100%)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '20px',
                      p: 3,
                      border: '1px solid rgba(42, 146, 53, 0.1)',
                      mb: 2,
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: 'linear-gradient(90deg, #2A9235 0%, #4CAF50 100%)',
                      }
                    }}>
                    {authen?.currentUser?.uid && !currentPlan ? (
                      <Box sx={{ py: 4 }}>
                        <CircularProgress size={50} sx={{ color: '#2A9235' }} />
                        <Typography sx={{ mt: 2, color: '#666' }}>Loading plan details...</Typography>
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ 
                          position: 'relative',
                          mb: 1
                        }}>
                          <Typography
                            variant="h4"
                            sx={{
                              fontSize: { xs: '20px', md: '24px' },
                              fontWeight: 800,
                              color: '#2A9235',
                              animation: `${slideInLeft} 0.6s ease-out`,
                              textAlign: 'center'
                            }}
                          >
                            {currentPlan?.title || defaultPlans[selectedTab].title}
                          </Typography>
                        </Box>

                        {/* Icon, Description, and Price in same line */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 2,
                          mb: 2,
                          position: 'relative'
                        }}>
                          {/* Description and Price on the left */}
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: '16px',
                                color: '#555',
                                mb: 1,
                                textAlign: 'left',
                                lineHeight: 1.4,
                                animation: `${slideInRight} 0.6s ease-out 0.2s both`
                              }}
                            >
                              {currentPlan?.description || defaultPlans[selectedTab].description}
                            </Typography>

                            <PriceDisplay sx={{ justifyContent: 'flex-start' }}>
                              <Typography className="price">
                                {currentPlan?.price || defaultPlans[selectedTab].price}
                              </Typography>
                              <Typography className="period">
                                {authen?.currentUser?.uid ? 
                                  ` / ${currentPlan?.period || defaultPlans[selectedTab].period}` :
                                  `${currentPlan?.currency || defaultPlans[selectedTab].currency} / ${currentPlan?.period || defaultPlans[selectedTab].period}`
                                }
                              </Typography>
                            </PriceDisplay>
                          </Box>
                          
                          {/* Icon on the far right */}
                          <img
                            src={LogoMember}
                            alt="premium"
                            style={{
                              height: '48px',
                              width: 'auto',
                              animation: `${float} 3s ease-in-out infinite`,
                              flexShrink: 0
                            }}
                          />
                        </Box>

                        {isCheckoutLoading ? (
                          <Box sx={{ py: 2 }}>
                            <CircularProgress size={40} sx={{ color: '#2A9235' }} />
                            <Typography sx={{ mt: 1, color: '#666' }}>Processing...</Typography>
                          </Box>
                        ) : (
                          <PremiumButton
                            fullWidth
                            onClick={handleUpgradeClick}
                            disabled={!!(authen?.currentUser?.uid && (
                              (selectedTab === 0 && !monthlySubscription?.priceId) ||
                              (selectedTab === 1 && !quarterlySubscription?.priceId) ||
                              (selectedTab === 2 && !yearlySubscription?.priceId)
                            ))}
                            sx={{
                              maxWidth: '400px',
                              mx: 'auto',
                              display: 'block',
                              animation: `${fadeInUp} 0.6s ease-out 0.4s both`
                            }}
                          >
                            {!authen?.currentUser?.uid ? 'Upgrade Now' : 
                             (!currentPlan || !currentPlan.priceId) ? 'Loading...' : 'Subscribe'}
                          </PremiumButton>
                        )}
                      </>
                    )}
                    </Box>
                  )}

                </Box>
              </CardContent>
            </GlassContainer>
          </Container>
        </MainContainer>
      </Box>
      <Footer />
      
      {/* Upgrade Popup */}
      <Dialog
        open={showUpgradePopup}
        onClose={handleCancelUpgrade}
        maxWidth="sm"
        fullWidth
        aria-labelledby="upgrade-dialog"
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(8px)',
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(0, 0, 0, 0.08)'
          }
        }}
      >
        <DialogTitle 
          id="upgrade-dialog" 
          sx={{ 
            textAlign: 'center',
            fontSize: '24px',
            fontWeight: 600,
            color: '#2A9235',
            py: 3,
            px: 3
          }}
        >
          Let's get you upgraded!
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 1 }}>
          <Typography 
            sx={{ 
              textAlign: 'center',
              fontSize: '16px',
              color: '#555',
              lineHeight: 1.5,
              mb: 2
            }}
          >
            Please log in to your account so we can take you straight to your Membership settings. From there, you can choose your plan and start enjoying premium benefits.
          </Typography>
        </DialogContent>
        <DialogActions 
          sx={{ 
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Button 
            onClick={handleCancelUpgrade} 
            color="primary" 
            variant="outlined"
            sx={{ 
              minWidth: '120px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              borderColor: '#2A9235',
              color: '#2A9235',
              '&:hover': {
                borderColor: '#228B22',
                backgroundColor: 'rgba(42, 146, 53, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleLoginAndContinue} 
            color="primary" 
            variant="contained"
            sx={{ 
              minWidth: '120px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              backgroundColor: '#2A9235',
              '&:hover': {
                backgroundColor: '#228B22'
              }
            }}
          >
            Log In & Continue
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Custom No Response Modal for mobile subscription management */}
      <CustomNoResponseModal
        isModalVisible={isQuestionModalVisible}
        onPress={hideNoQuestionModal}
        buttonText="OK"
        modalTitle="Manage Subscription"
        text={apiErrorMessage}
        color="success"
        icon={false}
      />
    </>
  );
}